import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import VisibilityIcon from '@mui/icons-material/Visibility'
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
  Chip,
  TextField,
  MenuItem,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  Typography,
} from '@mui/material'
import { useAtomValue } from 'jotai'
import React, { useState } from 'react'
import { useNavigate } from 'react-router'

import { useUsers } from '../../contexts/UserContext'
import { usersAtom } from '../../domains/user/AtomUsers'
import UserService from '../../services/UserService'
import UserCard from '../cards/UserCard'
import useBar from '../common/Bar'

// Role options for filtering and editing
const ROLE_OPTIONS = ['MEMBER', 'INSTRUCTOR', 'ADMIN']

export default function UserTable() {
  const navigate = useNavigate()

  const { updateUser, fetchAllUsers } = useUsers()
  const users = useAtomValue(usersAtom)

  const bar = useBar()

  // Table state
  const [orderBy, setOrderBy] = useState('firstName')
  const [order, setOrder] = useState('asc')
  const [roleFilter, setRoleFilter] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')

  // Dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [editRoles, setEditRoles] = useState([])

  // Handle sort
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  // Sort function
  const sortData = (data) => {
    if (data === undefined || data.length === 0) return []
    return data.sort((a, b) => {
      const aValue = orderBy === 'roles' ? a[orderBy][0] || '' : a[orderBy]
      const bValue = orderBy === 'roles' ? b[orderBy][0] || '' : b[orderBy]

      if (order === 'asc') {
        if (orderBy === 'value' || orderBy === 'clubMembershipId') {
          return (aValue || '').toString().localeCompare((bValue || '').toString())
        }
        return aValue < bValue ? -1 : 1
      } else {
        if (orderBy === 'value' || orderBy === 'clubMembershipId') {
          return (bValue || '').toString().localeCompare((aValue || '').toString())
        }
        return bValue < aValue ? -1 : 1
      }
    })
  }

  // Filter function
  const filterData = (data) => {
    if (data === undefined || data.length === 0) return []
    return data.filter((user) => {
      const matchesRole = roleFilter === 'all' || user.roles.includes(roleFilter)
      const searchTerms = searchQuery.toLowerCase().trim().split(/\s+/)

      const matchesSearch = searchTerms.every((term) => {
        return (
          user.firstName.toLowerCase().includes(term) ||
          user.lastName.toLowerCase().includes(term) ||
          user.email.toLowerCase().includes(term) ||
          user.value.toLowerCase().includes(term) || // User ID
          (user.clubMembershipId || '').toLowerCase().includes(term) // Member ID
        )
      })

      return matchesRole && matchesSearch
    })
  }

  // Handle edit dialog
  const handleEditClick = (user) => {
    setSelectedUser(user)
    setEditRoles(user.roles)
    setEditDialogOpen(true)
  }

  // Handle delete dialog
  const handleDeleteClick = (user) => {
    setSelectedUser(user)
    setDeleteDialogOpen(true)
  }

  // Handle role update
  const handleRoleUpdate = async () => {
    try {
      await updateUser({ userId: selectedUser.value, userData: { ...selectedUser, roles: editRoles } })
      bar.success('User roles updated successfully')
      setEditDialogOpen(false)
    } catch (error) {
      console.error('Failed to update user roles:', error)
      bar.error(error.message || 'Failed to update user roles')
    } finally {
      setSelectedUser(null)
      setEditRoles([])
    }
  }

  // Handle user deletion
  const handleDelete = async () => {
    if (!selectedUser) return

    try {
      await UserService.deleteUser(selectedUser.value)
      bar.success(`Successfully deleted user ${selectedUser.firstName} ${selectedUser.lastName}`)
      setDeleteDialogOpen(false)
      // Refresh the user list
      await fetchAllUsers({ forceRefresh: true })
    } catch (error) {
      console.error('Failed to delete user:', error)
      bar.error(error.message || 'Failed to delete user')
    } finally {
      setSelectedUser(null)
    }
  }

  // Process data with sorting and filtering
  const processedData = sortData(filterData(Object.values(users)))

  return (
    <Box>
      {/* Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: 300 }}
          placeholder="Search by name, email, user ID, or member ID"
          helperText="Multiple keywords supported (space separated)"
        />
        <FormControl size="small" sx={{ width: 150 }}>
          <InputLabel>Role Filter</InputLabel>
          <Select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} label="Role Filter">
            <MenuItem value="all">All Roles</MenuItem>
            {ROLE_OPTIONS.map((role) => (
              <MenuItem key={role} value={role}>
                {role}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table sx={{ tableLayout: 'fixed' }} size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '12%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', py: 1 }}>
                <TableSortLabel active={orderBy === 'firstName'} direction={orderBy === 'firstName' ? order : 'asc'} onClick={() => handleRequestSort('firstName')}>
                  First Name
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: '12%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', py: 1 }}>
                <TableSortLabel active={orderBy === 'lastName'} direction={orderBy === 'lastName' ? order : 'asc'} onClick={() => handleRequestSort('lastName')}>
                  Last Name
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: '20%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', py: 1 }}>
                <TableSortLabel active={orderBy === 'email'} direction={orderBy === 'email' ? order : 'asc'} onClick={() => handleRequestSort('email')}>
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: '15%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', py: 1 }}>
                <TableSortLabel active={orderBy === 'value'} direction={orderBy === 'value' ? order : 'asc'} onClick={() => handleRequestSort('value')}>
                  User ID
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: '12%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', py: 1 }}>
                <TableSortLabel
                  active={orderBy === 'clubMembershipId'}
                  direction={orderBy === 'clubMembershipId' ? order : 'asc'}
                  onClick={() => handleRequestSort('clubMembershipId')}
                >
                  Member ID
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: '17%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', py: 1 }}>
                <TableSortLabel active={orderBy === 'roles'} direction={orderBy === 'roles' ? order : 'asc'} onClick={() => handleRequestSort('roles')}>
                  Roles
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: '12%', whiteSpace: 'nowrap', py: 1 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {processedData.map((user) => (
              <TableRow key={user.value} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', py: 0.5 }}>{user.firstName}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', py: 0.5 }}>{user.lastName}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', py: 0.5 }}>{user.email}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', py: 0.5 }}>{user.value}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', py: 0.5 }}>{user.clubMembershipId || '-'}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', py: 0.5 }}>
                  <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'nowrap', overflow: 'hidden' }}>
                    {user.roles.map((role) => (
                      <Chip
                        key={role}
                        label={role}
                        size="small"
                        color={role === 'ADMIN' ? 'error' : role === 'INSTRUCTOR' ? 'primary' : 'default'}
                        sx={{
                          flexShrink: 0,
                          height: '20px',
                          '& .MuiChip-label': {
                            px: 1,
                            fontSize: '0.75rem',
                          },
                        }}
                      />
                    ))}
                  </Stack>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', py: 0.5 }}>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip
                      title={
                        <Box sx={{ p: 1 }}>
                          <UserCard user={user} size="small" />
                        </Box>
                      }
                      arrow
                      placement="left"
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: 'transparent',
                            maxWidth: 'none',
                            '& .MuiTooltip-arrow': {
                              color: 'transparent',
                            },
                          },
                        },
                      }}
                    >
                      <IconButton size="small" onClick={() => navigate(`/users/${user.value}`)} sx={{ p: 0.5 }}>
                        <VisibilityIcon sx={{ fontSize: '1.1rem' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Roles">
                      <IconButton size="small" onClick={() => handleEditClick(user)} sx={{ p: 0.5 }}>
                        <EditIcon sx={{ fontSize: '1.1rem' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDeleteClick(user)} sx={{ p: 0.5 }}>
                        <DeleteIcon sx={{ fontSize: '1.1rem' }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        aria-labelledby="edit-dialog-title"
        aria-describedby="edit-dialog-description"
        disablePortal={false}
        keepMounted
      >
        <DialogTitle id="edit-dialog-title">Edit User Roles</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="edit-roles-label">Roles</InputLabel>
              <Select
                labelId="edit-roles-label"
                multiple
                value={editRoles}
                onChange={(e) => setEditRoles(e.target.value)}
                label="Roles"
                renderValue={(selected) => (
                  <Stack direction="row" spacing={0.5}>
                    {selected.map((role) => (
                      <Chip key={role} label={role} size="small" />
                    ))}
                  </Stack>
                )}
              >
                {ROLE_OPTIONS.map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} color="inherit" autoFocus>
            Cancel
          </Button>
          <Button onClick={handleRoleUpdate} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        disablePortal={false}
        keepMounted
      >
        <DialogTitle id="delete-dialog-title">Delete User</DialogTitle>
        <DialogContent>
          <Typography id="delete-dialog-description">
            Are you sure you want to delete user {selectedUser?.firstName} {selectedUser?.lastName}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit" autoFocus>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
